import type { SupermetricsPlatform } from '../thirdParty/supermetrics'
import fetch from './fetch'

export const PlatformsList = [
  'adobe_analytics',
  'amazon',
  'analytics',
  'criteo',
  'dv360',
  'facebook',
  'ga4',
  'gcp',
  'google',
  'google_bigquery',
  'google_search_console',
  'google_sheets',
  'linkedin',
  'merchant_center',
  'microsoft_advertising',
  'pinterest_ads',
  'shopify',
  'snapchat',
  'tiktok',
  'pinterest',
  'chatgpt_ads'
] as const

export type Platforms = (typeof PlatformsList)[number] | SupermetricsPlatform

export const AlternatePlatformsList = [
  'google_analytics',
  'gads',
  'googleads',
  'google_ads',
  'bing',
  'linkedin_ads',
  'merchant'
] as const

type AlternatePlatforms = (typeof AlternatePlatformsList)[number]

export type ExtendedPlatforms = Platforms | AlternatePlatforms

type ExtendedPlatformMappings = Record<ExtendedPlatforms, Platforms>

export const PlatformMapping: ExtendedPlatformMappings = {
  adobe_analytics: 'adobe_analytics',
  amazon: 'amazon',
  analytics: 'analytics',
  google_analytics: 'analytics',
  criteo: 'criteo',
  dv360: 'dv360',
  facebook: 'facebook',
  ga4: 'ga4',
  gcp: 'gcp',
  google: 'google',
  gads: 'google',
  googleads: 'google',
  google_ads: 'google',
  google_bigquery: 'google_bigquery',
  google_search_console: 'google_search_console',
  google_sheets: 'google_sheets',
  linkedin: 'linkedin',
  linkedin_ads: 'linkedin',
  merchant_center: 'merchant_center',
  microsoft_advertising: 'microsoft_advertising',
  bing: 'microsoft_advertising',
  pinterest_ads: 'pinterest_ads',
  shopify: 'shopify',
  snapchat: 'snapchat',
  tiktok: 'tiktok',
  merchant: 'merchant_center',
  adtraction: 'adtraction',
  hubspot: 'hubspot',
  klaviyo: 'klaviyo',
  spotify_ads: 'spotify_ads',
  reddit_ads: 'reddit_ads',
  rtb_house: 'rtb_house',
  woocommerce: 'woocommerce',
  cj_affiliate: 'cj_affiliate',
  tradedoubler: 'tradedoubler',
  pinterest: 'pinterest',
  chatgpt_ads: 'chatgpt_ads',
  google_campaign_manager360: 'google_campaign_manager360',
  partnerize: 'partnerize',
  stackadapt: 'stackadapt',
  shopify_sm: 'shopify_sm',
  x_ads: 'x_ads',
  stripe: 'stripe',
  quantcast: 'quantcast'
}

const client = fetch()

export interface ClientAccountsResp {
  createdAt: string
  updatedAt: string
  id: number
  accountId: string
  name: string
  connected: boolean
  platform: Platforms
  thirdPartyProvider: string | null
  canManageClients: boolean
  credentialsId: string
  credentialsName: string
  externalAccountId: string
  externalParentAccountId: string | null
  /**
   * Google Ads only: the MCC the stored credential reaches this account through.
   * Optional rather than `string | null` because the UI and central_api do not
   * deploy atomically — during a rollout the key can be absent entirely.
   */
  loginCustomerId?: string | null
  /** Google Ads only: the account's reporting currency, as Google reports it. */
  currencyCode?: string | null
  recommendationsEngineActive: boolean
}

export interface AddAccountReq {
  clientId: number
  platform: string
  externalAccountId: string
  partnerId?: string
  has_360?: boolean
  externalPropertyId?: string
  externalParentAccountId?: string
  name?: string
  credentialsId?: string
  regionId?: string
  countryCode?: string
  canManageClients?: boolean
  thirdPartyProvider?: string
}

export interface ReconnectAccountReq {
  accountId: string
  credentialsId?: string | null
  clientId: number
}

export interface UpdateRecommendationsForAccountReq {
  accountId: number
  recommendationsEngineActive: boolean
  clientId: number
}

export interface AddAccountResp {
  displayName?: string
  partnerId?: string
  id: string
  name: string
}

export interface SpreadsheetTab {
  columnCount: number
  id: string
  index: number
  rowCount: number
  title: string
  type: string
}

export interface SpreadsheetMetadataResp {
  autoRecalc: string
  externalAccountId: string
  locale: string
  platform: string
  tabs: SpreadsheetTab[]
  timeZone: string
  title: string
  url: string
}

export interface GCPAccessControl {
  role: string
  specialGroup?: string
  userByEmail?: string
}

export interface GCPBigQueryDataset {
  access: GCPAccessControl[]
  creationTime: string
  datasetId: string
  defaultPartitionExpirationMs: number | null
  defaultTableExpirationMs: number | null
  description: string | null
  labels: Record<string, string>
  lastModifiedTime: string
  location: string
}

export interface GCPParentInfo {
  id: string
  type: string
}

export interface GCPMetadataResp {
  bigqueryDatasets: GCPBigQueryDataset[]
  createTime: string
  labels: Record<string, string>
  lifecycleState: string
  name: string
  parent: GCPParentInfo
  projectId: string
  projectNumber: string
  statusCode: number
}

export interface BigqueryTablesMetadata {
  tableId: string
  creationTime: string
}

export interface GCPDatasetMetadataResp {
  bigqueryTables: BigqueryTablesMetadata[]
  datasetDetails: GCPBigQueryDataset
}

export const paths = {
  accounts: '/accounts'
}

export const getClientAccounts = async (clientId: number, token?: string): Promise<ClientAccountsResp[]> => {
  return await client.get<ClientAccountsResp[]>({
    path: `${paths.accounts}?client_id=${clientId}`,
    token
  })
}

export const getClientAccountPlatform = async (clientId: number, platform: string): Promise<ClientAccountsResp[]> => {
  return await client.get<ClientAccountsResp[]>({
    path: `${paths.accounts}?client_id=${clientId}&platform=${platform}`
  })
}

export const addAccount = async (data: AddAccountReq, token?: string): Promise<AddAccountResp> => {
  const requestData: AddAccountReq = { ...data }
  // Merchant center name is retrieved in the backend after addAccount request is made, see:
  // https://github.com/Precis-Digital/precis-user-platform/blob/86628dda732c10b9d071df25b275a0d63f3d3f6e/server/accounts/service.py#L155-L158
  if (data.platform === 'merchant_center') {
    delete requestData.name
  }
  return await client.post({
    path: paths.accounts,
    data: requestData,
    token
  })
}
export const reconnectAccount = async (data: ReconnectAccountReq): Promise<AddAccountResp> => {
  const { accountId, credentialsId, clientId } = data
  return await client.patch({
    path: `${paths.accounts}/${accountId}?client_id=${clientId}`,
    data: {
      credentialsId
    }
  })
}

export const updateRecommendationsForAccount = async (
  data: UpdateRecommendationsForAccountReq
): Promise<AddAccountResp> => {
  const { accountId, recommendationsEngineActive, clientId } = data
  return await client.patch({
    path: `${paths.accounts}/${accountId}?client_id=${clientId}`,
    data: {
      recommendationsEngineActive
    }
  })
}

export const deleteClientAccount = async (clientId: number, accountId: number): Promise<ClientAccountsResp> => {
  return await client.del<ClientAccountsResp>({
    path: `${paths.accounts}/${accountId}?client_id=${clientId}`
  })
}

export const getGoogleSheetsAccountMetadata = async (
  clientId: number,
  accountId: number
): Promise<SpreadsheetMetadataResp> => {
  return await client.get<SpreadsheetMetadataResp>({
    path: `/accounts/${accountId}/google_sheets_metadata?client_id=${clientId}`
  })
}

export const getGCPAccountMetadata = async (clientId: number, accountId: number): Promise<GCPMetadataResp> => {
  return await client.get<GCPMetadataResp>({
    path: `/accounts/${accountId}/gcp_metadata?client_id=${clientId}`
  })
}

export const getGCPDatasetMetadata = async (
  clientId: number,
  accountId: number,
  datasetId: string
): Promise<GCPDatasetMetadataResp> => {
  return await client.get<GCPDatasetMetadataResp>({
    path: `/accounts/${accountId}/gcp_dataset_metadata?client_id=${clientId}&dataset_id=${datasetId}`
  })
}

export interface AdPreviewResp {
  preview: string
  previewLink: string
}

export const getAccountAdPreview = async (
  clientId: number,
  accountId: number,
  creativeId: string,
  platform: Platforms,
  adFormat?: string
): Promise<AdPreviewResp> => {
  const params = new URLSearchParams({
    client_id: clientId.toString(),
    platform
  })
  if (adFormat != null) {
    params.append('ad_format', adFormat)
  }
  return await client.get<AdPreviewResp>({
    path: `/accounts/${accountId}/creatives/${creativeId}/preview?${params.toString()}`
  })
}
